// Tonight, numbered (sprint 29 item 2, roadmap 61): one night of five doors per UTC day, the same visitors in the same order for everyone,
// drawn from a daily seed whose fingerprint is public from the start and whose seed and rolls appear on /verify once the day is over.
// Pure and browser-safe (game/lineup.js has the hash): the server and the verify page run the same code.
import { sha256Sync, unit } from './lineup.js';

export const TONIGHT = { doors: 5, epoch: '2026-10-14' }; // KNOCK #1 is the epoch day
const DAY_MS = 86400000;

export const dayOf = ms => new Date(ms).toISOString().slice(0, 10);
export const dayEndsAt = day => Date.parse(day + 'T00:00:00Z') + DAY_MS;
export const dayIndex = day => Math.round((Date.parse(day + 'T00:00:00Z') - Date.parse(TONIGHT.epoch + 'T00:00:00Z')) / DAY_MS);
export const numberOf = day => Math.max(1, dayIndex(day) + 1);

export const tonightHash = (seed, day) => sha256Sync(`${seed}|tonight|${day}`);
export const labels = {
  rule: day => `tonight:${day}:rule`,
  aliens: day => `tonight:${day}:aliens`,
  order: (day, i) => `tonight:${day}:order:${i}`,
  persona: (day, i) => `tonight:${day}:persona:${i}`,
};

// The night: tonight's rule (an index into the tell ids), how many of the five are visitors (2 or 3), where they stand (a shuffle), and a seed
// for each door's persona. Every draw is SHA-256("<seed>:<label>"), no other source.
export function derive(seed, day, tellIds) {
  const n = TONIGHT.doors, rolls = [];
  const roll = (label, make) => { const result = make(unit(seed, label)); rolls.push({ label, result }); return result; };
  const rule = roll(labels.rule(day), u => Math.floor(u * tellIds.length));
  const aliens = roll(labels.aliens(day), u => 2 + Math.floor(u * 2));
  const kinds = Array.from({ length: n }, (_, i) => (i < aliens ? 'alien' : 'human'));
  for (let i = 0; i < n - 1; i++) { // Fisher-Yates from the top: step i swaps position n-1-i with one of the first n-i
    const j = roll(labels.order(day, i), u => Math.floor(u * (n - i))), k = n - 1 - i;
    [kinds[k], kinds[j]] = [kinds[j], kinds[k]];
  }
  const doors = kinds.map((kind, i) => ({ n: i + 1, kind, personaSeed: String(roll(labels.persona(day, i), u => Math.floor(u * 2 ** 32))) }));
  return { day, number: numberOf(day), rule: tellIds[rule], ruleIndex: rule, doors, rolls };
}

// record: { day, hash, seed, tells: [ids], rule, doors: [{ n, kind }], rolls: [{ label, result }] }. seed overrides record.seed.
export function verifyTonight(record, seed = record.seed) {
  const hashMatch = tonightHash(seed, record.day) === record.hash;
  const again = derive(seed, record.day, record.tells ?? []), got = record.rolls ?? [], rows = [];
  for (let i = 0; i < Math.max(got.length, again.rolls.length); i++) {
    const g = got[i], w = again.rolls[i];
    rows.push({ label: g?.label ?? w?.label ?? '', claimed: g?.result, recomputed: w?.result, ok: !!g && !!w && g.label === w.label && g.result === w.result });
  }
  const doorsOk = (record.doors ?? []).length === again.doors.length && again.doors.every((d, i) => record.doors[i]?.kind === d.kind);
  const ruleOk = record.rule === again.rule;
  return { hashMatch, rows, doorsOk, ruleOk, again, ok: hashMatch && rows.every(r => r.ok) && doorsOk && ruleOk };
}

// ---- results ----
export const squareOf = right => (right ? '\u{1F7E9}' : '\u{1F7E5}');
// "KNOCK #12 <one square per door> 4/5": nothing about names, lines or who the visitors were.
export const shareLine = (number, results) => `KNOCK #${number} ${results.map(squareOf).join('')} ${results.filter(Boolean).length}/${results.length}`;
export const fooledPct = (wrong, played) => (played ? Math.round((100 * wrong) / played) : 0);
export const doorFooledLine = ({ pct, of }) => `This door fooled ${pct}% of ${of} ${of === 1 ? 'player' : 'players'}.`;
export const fooledLine =({ pct, of }) => `Fooled ${pct}% of ${of} ${of === 1 ? 'player' : 'players'} tonight.`;

// ---- the day streak: nights completed on consecutive days, with one free miss ----
// state: { n, last (day index of the last night completed), spare (the free miss is still unused) }. Missing one day uses the spare;
// a second missed day, in a row or later in the same streak, starts again at 1.
export const streakAfter = (state, day) => {
  const d = dayIndex(day);
  if (!state || d - state.last > 2 || (d - state.last === 2 && !state.spare)) return { n: 1, last: d, spare: true };
  if (d <= state.last) return state;
  return { n: state.n + 1, last: d, spare: state.spare && d - state.last === 1 };
};
// what a player sees on day `day` before playing: the streak still alive (playing tonight would carry it on), else 0
export const streakShown = (state, day) => {
  if (!state) return 0;
  const gap = dayIndex(day) - state.last;
  return gap <= 1 || (gap === 2 && state.spare) ? state.n : 0;
};
